import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import ProductionOrderDocumentRef from '../../components/reports/ProductionOrderDocumentRef';
import ReportStockItemLink from '../../components/reports/ReportStockItemLink';
import PageHeader from '../../components/PageHeader';
import FilterDrawer, { toFilterAction } from '../../components/FilterDrawer';
import useReportFilters from '../../utils/useReportFilters';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { toReportExportAction } from '../../components/ReportExportButton';
import { DateRangePicker } from '../../components/ui/date-range-picker';
import { reportsService } from '../../services/reportsService';
import { downloadReportCsv } from '../../utils/reportExport';
import { Input } from '../../components/ui/input';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { cn, formatBRL, formatDateBR, formatDuration, formatQuantity, localDateISO } from '../../lib/utils';

function currentMonthPeriod() {
    const now = new Date();
    return {
        from: localDateISO(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: localDateISO(now),
    };
}

function ProductionOrderReportsReport() {
    usePageTitle('Registros de OP');
    const { toast, toastApiError } = useToast();
    const filters = useReportFilters(() => ({ period: currentMonthPeriod(), product: '' }));
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const buildReportParams = useCallback(() => {
        const { period, product } = filters.applied;
        return { date_from: period.from, date_to: period.to, product: product || undefined };
    }, [filters.applied]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await reportsService.productionOrderReports(buildReportParams());
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            toastApiError(e, { title: 'Erro ao carregar', fallback: 'Não foi possível carregar o relatório' });
        } finally {
            setLoading(false);
        }
    }, [buildReportParams, toast]);

    useEffect(() => {
        load();
    }, [load]);

    const handleExport = async () => {
        setExporting(true);
        try {
            await downloadReportCsv(
                reportsService.productionOrderReportsCsv(buildReportParams()),
                'registros-op.csv',
            );
        } catch (e) {
            console.error(e);
            toastApiError(e, { title: 'Erro ao exportar', fallback: 'Não foi possível exportar o relatório' });
        } finally {
            setExporting(false);
        }
    };

    const thBase = 'h-[56px] text-sm font-medium text-gray-500 text-center p-0';
    const tdBase = 'h-[56px] text-sm text-gray-800 p-0';
    const tdCenter = 'tabular-nums';
    const tdEnd = 'justify-end tabular-nums text-right';
    const ROW_HEIGHT = 56;
    const thInner = 'h-[56px] min-h-[56px] w-full border-r border-b border-gray-200 px-2 box-border flex items-center justify-center';
    const tdInner = 'h-[56px] min-h-[56px] w-full border-r border-b border-gray-200 px-2 box-border flex items-center';

    const colWidths = [300, 110, 80, 200, 120, 140, 140, 120, 140];
    const colLeft = [0, colWidths[0], colWidths[0] + colWidths[1], colWidths[0] + colWidths[1] + colWidths[2]];

    const stickyTop = {
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: '#f9fafb',
    };
    const stickyTopWithLeft = (left, width) => ({
        ...stickyTop,
        left,
        minWidth: width,
        width,
        zIndex: 11,
    });
    const stickyBody = (left, width) => ({
        position: 'sticky',
        left,
        minWidth: width,
        width,
        background: '#fff',
        zIndex: 5,
    });
    const normalCol = (width) => ({ minWidth: width, width });

    const totalCols = 9;

    return (
        <AppLayout>
            <div className="h-[calc(100vh-8rem)] flex flex-col gap-0">
                <PageHeader
                    title="Registros de OP"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Relatórios' },
                    ]}
                    additionalActions={[toFilterAction({ count: filters.count, onClick: filters.openDrawer })]}
                    actions={[
                        toReportExportAction({
                            onExport: handleExport,
                            disabled: loading,
                            exporting,
                        }),
                    ]}
                />
                <div className="overflow-auto flex-1 min-h-0 -mx-4 md:-mx-8 -mb-8">
                    {loading && rows.length === 0 ? null : (
                        <table className="w-full caption-bottom text-sm">
                            <TableHeader className="border-b-0 bg-transparent">
                                <TableRow className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[0], colWidths[0])}>
                                        <div className={cn(thInner, 'report-first-col')}>Item</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[1], colWidths[1])}>
                                        <div className={thInner}>Data</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={stickyTopWithLeft(colLeft[2], colWidths[2])}>
                                        <div className={thInner}>Duração</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[3]) }}>
                                        <div className={thInner}>Documento</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[4]) }}>
                                        <div className={thInner}>Qtd. produzida</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[5]) }}>
                                        <div className={cn(thInner, 'justify-end')}>Custo de Insumos</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[6]) }}>
                                        <div className={cn(thInner, 'justify-end')}>Custo de Recursos</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[7]) }}>
                                        <div className={cn(thInner, 'justify-end')}>Custo Unitário</div>
                                    </TableHead>
                                    <TableHead className={thBase} style={{ ...stickyTop, ...normalCol(colWidths[8]) }}>
                                        <div className={cn(thInner, 'justify-end border-r-2 border-r-gray-300')}>Custo Total</div>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow className="border-b-0 hover:bg-transparent">
                                        <TableCell colSpan={totalCols} className="text-center py-8 text-gray-500">
                                            Nenhum registro de produção no período
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row) => (
                                        <TableRow key={row.id} className="border-b-0 hover:bg-transparent" style={{ height: ROW_HEIGHT }}>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[0], colWidths[0])}>
                                                <div className={cn(tdInner, 'report-first-col')}>
                                                    <ReportStockItemLink productId={row.product_id}>
                                                        {row.item}
                                                    </ReportStockItemLink>
                                                </div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[1], colWidths[1])}>
                                                <div className={tdInner}>{formatDateBR(row.datetime)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={stickyBody(colLeft[2], colWidths[2])}>
                                                <div className={cn(tdInner, tdCenter)}>{formatDuration(row.duration_minutes)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[3])}>
                                                <div className={cn(tdInner, 'min-w-0')}>
                                                    {row.production_order_id != null ? (
                                                        <ProductionOrderDocumentRef
                                                            productionOrderId={row.production_order_id}
                                                            reportId={row.id}
                                                        />
                                                    ) : (
                                                        '-'
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[4])}>
                                                <div className={cn(tdInner, tdCenter, 'justify-center')}>{formatQuantity(row.produced_quantity)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[5])}>
                                                <div className={cn(tdInner, tdEnd)}>{formatBRL(row.supply_cost)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[6])}>
                                                <div className={cn(tdInner, tdEnd)}>{formatBRL(row.resource_cost)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[7])}>
                                                <div className={cn(tdInner, tdEnd)}>{formatBRL(row.unit_cost)}</div>
                                            </TableCell>
                                            <TableCell className={tdBase} style={normalCol(colWidths[8])}>
                                                <div className={cn(tdInner, tdEnd, 'border-r-2 border-r-gray-300')}>
                                                    {formatBRL(row.total_cost)}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </table>
                    )}
                </div>
            </div>
            <FilterDrawer open={filters.open} onClose={filters.close} onApply={filters.apply} onClear={filters.clear}>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
                    <DateRangePicker
                        className="w-full"
                        value={filters.draft.period}
                        onChange={(v) => filters.set('period', v)}
                        aria-label="Período"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Produto</label>
                    <Input value={filters.draft.product} onChange={(e) => filters.set('product', e.target.value)} />
                </div>
            </FilterDrawer>
        </AppLayout>
    );
}

export default ProductionOrderReportsReport;
